import * as React from 'react';

import { Listbox } from '@headlessui/react';

import { ChevronDown } from '@freelancelabs/icons';
import { formatList } from '@freelancelabs/shared';
import { clsx } from '@freelancelabs/utils';

import { usePopperAnchor } from '../popper';
import SelectOptions, { SelectOption } from '../select/SelectOptions';

export interface SelectProps<T> {
    options: SelectOption<T>[];
    value?: SelectOption<T> | SelectOption<T>[];
    onChange: (arg: SelectOption<T>) => void;
    placeholder?: string;
    multiple?: boolean;
    error?: React.ReactNode;
    className?: string;
    /** What the list says when `options` is empty. See `SelectOptions`. */
    emptyLabel?: React.ReactNode;
    /**
     * Put on the **button that opens the list**, which is what a page's
     * `<label htmlFor>` has to point at: `button` is a labelable element, the
     * surrounding `div` is not.
     *
     * Until it existed this control could not be given an accessible name at all:
     * a page wrote its label next to the field, the label bound to nothing, and the
     * control announced itself by its placeholder or by silence. `SelectNative`
     * inherits every `aria-*` from `SelectHTMLAttributes` and never had the problem,
     * which is why the gap went unseen.
     */
    id?: string;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    'aria-describedby'?: string;
}

const Select = <T,>({
    options,
    value,
    onChange,
    placeholder,
    multiple,
    error,
    className,
    emptyLabel,
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
}: SelectProps<T>) => {
    const { anchorRef } = usePopperAnchor<HTMLButtonElement>();
    const isEmpty = Array.isArray(value) ? !value.length : !value;
    const formatted = Array.isArray(value) ? formatList(value.map((v) => v.label)) : value?.label;

    /**
     * `undefined` means « uncontrolled » to headlessui: it then keeps a selection of
     * its own and stops following the prop, so a value arriving later: from a fetch,
     * or from the page's own state: leaves the list ticking the wrong row. `null`,
     * and `[]` when multiple, are the controlled way of saying « nothing selected ».
     */
    const selected = multiple ? (Array.isArray(value) ? value : []) : (value ?? null);

    /**
     * Attention, built by omission rather than passed as four props with `undefined` values:
     * headlessui computes `aria-labelledby` and `aria-expanded` on this button itself,
     * and merges the caller's props **over** its own. A literal `aria-labelledby={undefined}`
     * is a key that exists, so it would erase what the library put there: a control
     * that loses its name the moment a page gives it an `id`.
     */
    const labelling = {
        ...(id !== undefined && { id }),
        ...(ariaLabel !== undefined && { 'aria-label': ariaLabel }),
        ...(ariaLabelledBy !== undefined && { 'aria-labelledby': ariaLabelledBy }),
        ...(ariaDescribedBy !== undefined && { 'aria-describedby': ariaDescribedBy }),
    };

    return (
        // headlessui v2 infere le type de value depuis onChange (mode simple) :
        // le cast couvre le mode multiple, gere au runtime par la prop `multiple`
        <Listbox value={selected as SelectOption<T> | undefined} onChange={onChange} multiple={multiple} by="value">
            {({ open }) => (
                <>
                    <Listbox.Button
                        ref={anchorRef}
                        {...labelling}
                        className={clsx('input-wrapper', open && 'is-focus', Boolean(error) && 'error', className)}
                    >
                        <div className="select input">
                            {!isEmpty && formatted}
                            {isEmpty && <span className="input-placeholder">{placeholder}</span>}
                            <ChevronDown className={clsx('select-icon', open && 'is-open')} />
                        </div>
                    </Listbox.Button>
                    <SelectOptions
                        options={options}
                        anchorRef={anchorRef}
                        isOpen={open}
                        emptyLabel={emptyLabel}
                    ></SelectOptions>
                </>
            )}
        </Listbox>
    );
};

export default Select;
